import { formatLocalDateKey } from "../../utils/dateRangeUtils"
import { dashboardPlatformKeyFromDescription } from "./socialNetworkCatalog"

const STORY_SEGMENT_COLORS = ["#5320A4", "#6B2FC4", "#8745D8", "#A66FE8", "#C9A8F0", "#E4D0FA"]
export const STORY_AGGREGATION_MODE = "paid_and_organic"

function isPaidAndOrganicStoryMode(mode) {
  const normalized = String(mode ?? "").trim().toLowerCase().replace(/-/g, "_")
  return (
    normalized === "paid_and_organic" ||
    normalized === "paidandorganic" ||
    normalized === "paid_and_media" ||
    normalized === "paidandmedia"
  )
}

/** Story API can return organic_only duplicates — keep paid_and_organic only. */
export function filterStoryPerformanceRows(rows) {
  const list = Array.isArray(rows) ? rows : []
  const hasAggregationModes = list.some(
    (row) => row?.aggregation_mode != null || row?.aggregationMode != null,
  )
  if (!hasAggregationModes) return list
  return list.filter((row) =>
    isPaidAndOrganicStoryMode(row?.aggregation_mode ?? row?.aggregationMode),
  )
}

/** ISO date param for GET …/story/performance?startDate=&endDate= */
export function formatStoryPerformanceDate(date) {
  return formatLocalDateKey(date)
}

export function buildClientStoryPerformanceQuery({
  startDate,
  endDate,
  aggregationMode = STORY_AGGREGATION_MODE,
} = {}) {
  const params = {
    aggregation_mode: aggregationMode || STORY_AGGREGATION_MODE,
  }
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  return new URLSearchParams(params).toString()
}

export function buildStoryPerformanceFetchKey(clientId, startDate, endDate) {
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}|${STORY_AGGREGATION_MODE}`
}

export function unwrapStoryPerformancePayload(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.stories)) return data.stories
  if (Array.isArray(data?.data?.stories)) return data.data.stories
  if (Array.isArray(data?.data)) return data.data
  return []
}

/** Story metrics live on `latest_value`; `new_value` is often null on this API. */
function metricLatest(results, key) {
  const entry = results?.[key]
  if (!entry || typeof entry !== "object") return null
  const raw = entry.latest_value ?? entry.latestValue ?? entry.new_value ?? entry.newValue
  if (raw == null || raw === "") return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Serializable story date for Redux (`YYYY-MM-DD`). */
export function storyDateValue(value) {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalDateKey(value) || null
  }
  const raw = String(value).trim()
  const dateOnly = raw.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  return formatLocalDateKey(parsed) || null
}

function storyDateSortTime(value) {
  const key = storyDateValue(value)
  if (!key) return 0
  const [year, month, day] = key.split("-").map(Number)
  return new Date(year, month - 1, day).getTime()
}

/** API retention is a 0–1 ratio; the card shows an integer percent. */
export function storyRetentionPercent(raw) {
  const n = Number(raw)
  if (!Number.isFinite(n)) return 0
  if (n > 0 && n <= 1) return Math.round(n * 100)
  return Math.round(n)
}

function isStoryFrameRow(row) {
  return Number(row?.series_position) >= 1 && row?.post_id != null && row?.post_id !== ""
}

function mapStoryFrameToCard(row, index) {
  const impressions = metricLatest(row.results, "story_impressions") || 0
  const reach = metricLatest(row.results, "story_reach") || 0
  const replies = metricLatest(row.results, "story_replies") || 0
  const retentionRate = storyRetentionPercent(metricLatest(row.results, "story_retention_rate"))
  const thumbnail = String(row.post_media_link || "").trim()
  const permalink = String(row.post_link || "").trim()
  const platform = dashboardPlatformKeyFromDescription(row.social_network) || "instagram"
  const postId = String(row.post_id)
  const seriesId = String(row.series_id || postId)

  return {
    platform,
    storyIndex: `${seriesId}-${postId}-${index}`,
    date: storyDateValue(row.post_date),
    storyCount: 1,
    impressions,
    reach,
    replies,
    retentionRate,
    impressionsPerStory: [impressions],
    storyThumbnails: thumbnail ? [thumbnail] : [],
    segmentColors: STORY_SEGMENT_COLORS.slice(0, 1),
    permalink,
  }
}

/**
 * One card per story frame (series_position >= 1 with a post_id).
 * series_position 0 / null post_id rows are series totals and are skipped.
 */
export function normalizeStoryPerformancePayload(data) {
  const rows = filterStoryPerformanceRows(unwrapStoryPerformancePayload(data))
  const frames = rows.filter(isStoryFrameRow)

  frames.sort((a, b) => {
    const aTime = storyDateSortTime(a.post_date)
    const bTime = storyDateSortTime(b.post_date)
    if (bTime !== aTime) return bTime - aTime
    return Number(a.series_position) - Number(b.series_position)
  })

  return frames.map((row, index) => mapStoryFrameToCard(row, index))
}

export function resolveStoryPerformance(slice) {
  if (Array.isArray(slice?.stories)) return slice.stories
  return []
}
